import { useState, useEffect, useContext, useMemo } from 'react'
import { useLocation, useParams, useNavigate } from 'react-router-dom'

import { AppContext } from '/providers/app-context'
import { getPass } from '/providers/passes-api'
import { getSmartixScannerPassHistory } from '/providers/studio-api'

import ManagePassSubpageLayout from '/components/passes/manage-pass-subpage-layout'
import ActionErrorDialog from '/components/action-error-dialog'
import useActionErrorDialog from '/components/use-action-error-dialog'
import HelpText from '/help/pass-scans'
import ScanTableEvent from '../components/passes/scan-table-event'
import ScanTableStamp from '../components/passes/scan-table-stamp'
import ScanTableGiftcard from '../components/passes/scan-table-giftcard'
import ScanTableLoyalty from '../components/passes/scan-table-loyalty'
import ScanTableMembership from '../components/passes/scan-table-membership'

const PassScans = () => {

  const navigate = useNavigate()

  const { templateId, passId } = useParams()
  const { apiSource, apiKey, templates, passes, setPasses } = useContext(AppContext)
  const { actionError, errorDialogOpen, setErrorDialogOpen, showActionError } = useActionErrorDialog()

  const template = useMemo(() => { return templates.filter(t => t.template_id === templateId)[0] }, [templates, templateId])

  const location = useLocation()

  const [pass, setPass] = useState(() => location.state?.pass ?? passes.find(item => item.template_id === templateId && item.pass_id === passId))
  const [scans, setScans] = useState([])
  const [loading, setLoading] = useState(true)

  const loadedPass = pass?.pass_id === passId ? pass : undefined

  useEffect(() => {
    if (!template || !loadedPass) return
    let active = true

    const loadScans = async () => {
      const history = await getSmartixScannerPassHistory({ apiSource, apiKey, passType: template.pass_type_id, templateId: loadedPass.template_id, passId: loadedPass.pass_id })
      if (!active) return
      if (Array.isArray(history)) {
        setScans(history)
      } else if (Array.isArray(history?.scans)) {
        setScans(history.scans)
      } else {
        setScans([])
        if (Number(history?.error?.status_code) !== 429) showActionError(history?.error, 'Error loading scan history')
      }
      setLoading(false)
    }

    loadScans()
    return () => { active = false }
  }, [apiKey, apiSource, loadedPass, template])

  useEffect(() => {
    if (loadedPass) return
    let active = true

    const loadPass = async () => {
      const result = await getPass({ apiSource, apiKey, passId })
      if (!active) return
      if (result.ok === false || !result.pass) return navigate("/templates")

      setPasses(current => {
        const idx = current.findIndex(item => item.template_id === templateId && item.pass_id === passId)
        if (idx < 0) return [...current, result.pass]
        const next = [...current]
        next[idx] = result.pass
        return next
      })
      setPass(result.pass)
    }

    loadPass()
    return () => { active = false }
  }, [apiKey, apiSource, loadedPass, navigate, passId, setPasses, templateId])


  if (template && loadedPass) {
    const renderScanTable = ScanTable => (
      <ManagePassSubpageLayout title={`Scan History (${pass?.pass_id})`} HelpText={HelpText} heading={`${loadedPass.pass_id} ${loadedPass.mode === "test" ? " [test]" : ""}`} template={template} pass={loadedPass} onClose={() => navigate(`/templates/${templateId}/passes`)}>
        <ScanTable scans={scans} loading={loading} templateId={templateId} passId={loadedPass.pass_id} />
        <ActionErrorDialog title="Scan history failed" open={errorDialogOpen} setOpen={setErrorDialogOpen} error={actionError} />
      </ManagePassSubpageLayout>
    )

    if (template.pass_type_id.replace("_bg","") === "managed_event") return renderScanTable(ScanTableEvent)

    if (template.pass_type_id === "managed_gift") return renderScanTable(ScanTableGiftcard)

    if (template.pass_type_id === "managed_stamp") return renderScanTable(ScanTableStamp)

    if (template.pass_type_id === "managed_loyalty") return renderScanTable(ScanTableLoyalty)

    if (template.pass_type_id.replace("_sc","") === "managed_membership") return renderScanTable(ScanTableMembership)

  }
}

export default PassScans
